import React, { useContext } from "react";
import { Alert, Box, Button, Dialog, DialogContent, Paper, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import currency from "currency.js";
import { Link as RouterLink } from "react-router-dom";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import SendReceipt from "../../order/component/SendReceipt";

/**
 * Payment outcome UI. Layout is driven by remaining balance + fulfillment type:
 * - to-go with balance due → warning + collect-payment only (no receipt chrome)
 * - otherwise → success chrome + New Sale / Done (+ optional Add Payment for non-togo balance)
 *
 * Pass `order` when available (uses order.payment.due + order.fulfillment.type);
 * otherwise `result.remainingDue` + `result.fulfillmentType`.
 */
export default function PaymentSuccessScreen(props) {
  const {
    open,
    onClose,
    result = {},
    order,
    title,
    newSaleTo,
    onDone,
    onCollectPayment,
    collectPaymentLoading = false,
    collectPaymentError = "",
  } = props;
  const { user } = useContext(UserContext);

  const remainingDue =
    result && Object.prototype.hasOwnProperty.call(result, "remainingDue")
      ? +result.remainingDue || 0
      : +order?.payment?.due || 0;
  const fulfillmentType = order?.fulfillment?.type || result.fulfillmentType || "";
  const isTogoBalanceDue = fulfillmentType === "togo" && remainingDue > 0;

  const hasReceiptPrinter = Boolean(user.config?.receiptPrinterId);
  const heading = title || (result.quoteId ? "Invoice Sent" : "Payment Received");

  const printReceipt = () => {
    bkstApi
      .post(
        `/my/printer/${user.config.receiptPrinterId}/job?orderId=${result.orderId || ""}&paymentIntentId=${result.paymentIntentId || ""}`
      )
      .then(() => {});
  };

  const printPackingSlip = () => {
    bkstApi
      .post(`/my/printer/${user.config.receiptPrinterId}/packing-slip?orderId=${result.orderId}`)
      .then(() => {});
  };

  if (isTogoBalanceDue) {
    return (
      <Dialog
        fullScreen
        open={open}
        scroll="paper"
        onClose={() => {}}
        PaperProps={{ sx: { backgroundColor: "#FAFAFA" } }}
      >
        <DialogContent sx={{ px: 2 }}>
          <Box
            pt={6}
            pb={4}
            sx={{
              width: "400px",
              maxWidth: "100%",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: "50%",
                backgroundColor: "rgba(211, 47, 47, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: "2.75rem", color: "error.main" }} />
            </Box>

            <Typography variant="overline" color="error" sx={{ letterSpacing: "0.12em" }}>
              Balance Due
            </Typography>

            <Typography variant="h2" color="error" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
              {currency(remainingDue).format()}
            </Typography>

            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 320, mb: 1 }}>
              Partial payment received. Collect balance due to complete the sale.
            </Typography>

            {onCollectPayment && (
              <Button
                fullWidth
                size="large"
                color="primary"
                variant="contained"
                onClick={onCollectPayment}
                disabled={collectPaymentLoading}
                sx={{ mt: 3, py: 1.5, fontSize: "1.1rem" }}
              >
                {collectPaymentLoading ? "Loading…" : "Take Another Payment"}
              </Button>
            )}
            {collectPaymentError && (
              <Alert severity="error" sx={{ justifyContent: "center", mt: 1.5, width: "100%" }}>
                {collectPaymentError}
              </Alert>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog fullScreen open={open} scroll="paper" onClose={onClose}>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CheckCircleIcon sx={{ fontSize: "5rem", color: "#16a085" }} />
          <Typography variant="h5" color="textSecondary">
            {heading}
          </Typography>
          {result.cash && (
            <Typography variant="body1" color="textSecondary">
              Cash Tendered: {currency(result.cash.tendered).format()} | Change Due:{" "}
              {currency(result.cash.change).format()}
            </Typography>
          )}
          <Box
            my={2}
            sx={{
              width: "400px",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
            }}
          >
            {newSaleTo ? (
              <Button
                fullWidth
                size="large"
                color="primary"
                variant="contained"
                component={RouterLink}
                to={newSaleTo}
              >
                New Sale
              </Button>
            ) : (
              onDone && (
                <Button fullWidth size="large" color="primary" variant="contained" onClick={onDone}>
                  Done
                </Button>
              )
            )}
            {remainingDue > 0 && onCollectPayment && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="body1" color="textSecondary" textAlign="center">
                  Balance Due {currency(remainingDue).format()}
                </Typography>
                <Button
                  fullWidth
                  size="large"
                  color="secondary"
                  variant="outlined"
                  onClick={onCollectPayment}
                  disabled={collectPaymentLoading}
                >
                  {collectPaymentLoading ? "Loading…" : "Add Payment"}
                </Button>
              </Box>
            )}
            {collectPaymentError && (
              <Alert severity="error" sx={{ justifyContent: "center" }}>
                {collectPaymentError}
              </Alert>
            )}
            {(result.orderId || result.paymentIntentId) && (
              <>
                <Box>
                  <Typography
                    variant="overline"
                    color="textSecondary"
                    sx={{ display: "block", mb: 0.75, letterSpacing: "0.08em" }}
                  >
                    Send Receipt
                  </Typography>
                  <Paper elevation={0} sx={{ backgroundColor: "#F7F7F7", borderRadius: "8px" }}>
                    <Box p={1.5} sx={{ display: "flex", gap: "0.75rem" }}>
                      <SendReceipt
                        fullWidth
                        orderId={result.orderId}
                        paymentIntentId={result.paymentIntentId}
                        mode="email"
                        email={result.email}
                      >
                        <Button
                          fullWidth
                          size="large"
                          color="secondary"
                          variant="contained"
                          disableElevation
                        >
                          Email
                        </Button>
                      </SendReceipt>
                      <SendReceipt
                        fullWidth
                        orderId={result.orderId}
                        paymentIntentId={result.paymentIntentId}
                        mode="sms"
                        phone={result.phone}
                      >
                        <Button
                          fullWidth
                          size="large"
                          color="secondary"
                          variant="contained"
                          disableElevation
                        >
                          Text
                        </Button>
                      </SendReceipt>
                    </Box>
                  </Paper>
                </Box>
                <Box>
                  <Typography
                    variant="overline"
                    color="textSecondary"
                    sx={{ display: "block", mb: 0.75, letterSpacing: "0.08em" }}
                  >
                    Print
                  </Typography>
                  <Paper elevation={0} sx={{ backgroundColor: "#F7F7F7", borderRadius: "8px" }}>
                    <Box p={1.5} sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                      <Box sx={{ display: "flex", gap: "0.75rem" }}>
                        <Button
                          fullWidth
                          size="large"
                          color="secondary"
                          variant="contained"
                          disableElevation
                          onClick={() => printReceipt()}
                          disabled={!hasReceiptPrinter}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          Receipt
                        </Button>
                        {result.orderId && (
                          <Button
                            fullWidth
                            size="large"
                            color="secondary"
                            variant="contained"
                            disableElevation
                            onClick={() => printPackingSlip()}
                            disabled={!hasReceiptPrinter}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            Packing Slip
                          </Button>
                        )}
                      </Box>
                      {!hasReceiptPrinter && (
                        <Typography variant="caption" color="textSecondary" sx={{ px: 0.5 }}>
                          Requires a receipt printer
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Box>
              </>
            )}
            {result.orderId && (
              <Button
                fullWidth
                size="large"
                color="secondary"
                variant="outlined"
                component={RouterLink}
                to={`/order/${result.orderId}`}
              >
                View Order
              </Button>
            )}
            {!result.orderId && result.paymentIntentId && (
              <Button
                fullWidth
                size="large"
                color="secondary"
                variant="outlined"
                to={`/redirect?from=pi&id=${result.paymentIntentId}&to=order`}
                component={RouterLink}
              >
                View Order
              </Button>
            )}
            {result.quoteId && (
              <Button
                fullWidth
                size="large"
                color="secondary"
                variant="outlined"
                component={RouterLink}
                to={`/quote/${result.quoteId}`}
              >
                View Invoice
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
